// src/pages/State-Master/StateMaster.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
} from 'react-icons/md';
import {
  FaPlus,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaGlobeAmericas,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#2563eb' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  }),
};

const ZONE_OPTIONS = [
  { value: 'North', label: 'North' },
  { value: 'South', label: 'South' },
  { value: 'East', label: 'East' },
  { value: 'West', label: 'West' },
  { value: 'Central', label: 'Central' },
  { value: 'North-East', label: 'North-East' },
];

const initialFormState = {
  statename: '',
  zone: '',
  statecode: '',
  status: true,
};

const StateMaster = () => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Search, Filter & View Controls
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [zoneFilter, setZoneFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [viewState, setViewState] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchStates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch states');
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error loading states');
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('token')) fetchStates();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, zoneFilter]);

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!form.statename?.trim()) errors.statename = 'State Name is required';
    if (!form.zone?.trim()) errors.zone = 'Zone is required';
    if (form.statecode && form.statecode.length > 10) errors.statecode = 'Max 10 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenModal = (state = null) => {
    setFormErrors({});
    if (state) {
      setEditingState(state);
      setForm({
        statename: state.statename || '',
        zone: state.zone || '',
        statecode: state.statecode || '',
        status: state.status !== false,
      });
    } else {
      setEditingState(null);
      setForm(initialFormState);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingState(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        statename: form.statename.trim(),
        zone: form.zone.trim(),
        statecode: form.statecode ? form.statecode.trim().toUpperCase() : '',
        status: form.status,
      };

      const url = editingState ? `${API_BASE_URL}/state/${editingState._id}` : `${API_BASE_URL}/state`;
      const method = editingState ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Operation failed');

      toast.success(editingState ? 'State updated successfully!' : 'State created successfully!');
      handleCloseModal();
      fetchStates();
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete State?',
      text: `Are you sure you want to delete ${name || 'this state'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/state/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to delete state');
        toast.success('State deleted successfully');
        fetchStates();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    if (states.length === 0) {
      toast.info('No state data to export');
      return;
    }
    const exportRows = filteredStates.map((s, idx) => ({
      'S.No': idx + 1,
      'State Name': s.statename || '-',
      Zone: s.zone || '-',
      'State Code': s.statecode || '-',
      Status: s.status !== false ? 'Active' : 'Inactive',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'States');
    XLSX.writeFile(workbook, `State_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('State list exported to Excel successfully!');
  };

  // Filter Logic
  const filteredStates = useMemo(() => {
    return states.filter((s) => {
      const isActive = s.status !== false;
      if (statusFilter === 'ACTIVE' && !isActive) return false;
      if (statusFilter === 'INACTIVE' && isActive) return false;

      if (zoneFilter !== 'ALL' && s.zone?.toLowerCase() !== zoneFilter.toLowerCase()) {
        return false;
      }

      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      return (
        (s.statename || '').toLowerCase().includes(term) ||
        (s.zone || '').toLowerCase().includes(term) ||
        (s.statecode || '').toLowerCase().includes(term)
      );
    });
  }, [states, searchTerm, statusFilter, zoneFilter]);

  const totalItems = filteredStates.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredStates.slice(start, start + pageSize);
  }, [filteredStates, currentPage, pageSize]);

  const totalActive = useMemo(() => states.filter((s) => s.status !== false).length, [states]);
  const totalInactive = states.length - totalActive;
  const uniqueZones = useMemo(() => [...new Set(states.map((s) => s.zone).filter(Boolean))], [states]);

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header & KPI Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            State Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage operating states, geographical zones and regional codes
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaPlus className="text-sm" />
            <span>Add New State</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total States</p>
            <p className="text-2xl font-bold text-gray-800">{states.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaGlobeAmericas />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active</p>
            <p className="text-2xl font-bold text-green-600">{totalActive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaCheckCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Inactive</p>
            <p className="text-2xl font-bold text-gray-500">{totalInactive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaTimesCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Zones</p>
            <p className="text-2xl font-bold text-indigo-600">{uniqueZones.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaMapMarkerAlt />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="relative flex-1 min-w-[260px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by state name, zone, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400">
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* Zone Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Zone:</span>
              <select
                value={zoneFilter}
                onChange={(e) => setZoneFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Zones</option>
                {uniqueZones.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
            </div>

            {(searchTerm || statusFilter !== 'ALL' || zoneFilter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setZoneFilter('ALL');
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 ml-auto lg:ml-2">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading states...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaGlobeAmericas className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No states found</p>
          </div>
        ) : viewMode === 'card' ? (
          /* Landscape Cards View */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
            {paginatedData.map((s, idx) => {
              const isActive = s.status !== false;
              const sNo = (currentPage - 1) * pageSize + idx + 1;

              return (
                <div
                  key={s._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 p-5 flex flex-col justify-between transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-base flex items-center justify-center shadow-sm">
                          {s.statename?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base leading-tight">
                            {s.statename}
                          </h3>
                          <span className="text-xs text-gray-400 font-medium">#{sNo}</span>
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Zone</span>
                        <span className="font-semibold text-gray-800">{s.zone || '-'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">State Code</span>
                        <span className="font-mono font-bold text-blue-600">{s.statecode || '-'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => setViewState(s)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    >
                      <FaEye /> <span>View</span>
                    </button>
                    <button
                      onClick={() => handleOpenModal(s)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                    >
                      <MdEdit /> <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(s._id, s.statename)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table List View */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">State Name</th>
                  <th className="py-3 px-4">Zone</th>
                  <th className="py-3 px-4">State Code</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedData.map((s, idx) => {
                  const isActive = s.status !== false;
                  const sNo = (currentPage - 1) * pageSize + idx + 1;

                  return (
                    <tr key={s._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3 px-4 text-center text-gray-500 font-semibold">{sNo}</td>
                      <td className="py-3 px-4 font-bold text-gray-900">{s.statename}</td>
                      <td className="py-3 px-4 font-medium text-gray-700">{s.zone}</td>
                      <td className="py-3 px-4 font-mono font-semibold text-blue-600">{s.statecode || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button onClick={() => setViewState(s)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <FaEye />
                          </button>
                          <button onClick={() => handleOpenModal(s)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                            <MdEdit />
                          </button>
                          <button onClick={() => handleDelete(s._id, s.statename)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((pageNum, idx, arr) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Add/Edit State */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaGlobeAmericas className="text-xl" />
                <h3 className="text-xl font-bold">{editingState ? 'Edit State' : 'Add New State'}</h3>
              </div>
              <button onClick={handleCloseModal} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    State Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Tamil Nadu"
                    value={form.statename}
                    onChange={(e) => setForm({ ...form, statename: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.statename ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.statename && <p className="text-red-500 text-xs mt-1">{formErrors.statename}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Zone <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={ZONE_OPTIONS}
                    value={ZONE_OPTIONS.find((z) => z.value === form.zone) || (form.zone ? { value: form.zone, label: form.zone } : null)}
                    onChange={(opt) => setForm({ ...form, zone: opt ? opt.value : '' })}
                    placeholder="Search / select zone..."
                    styles={customSelectStyles}
                  />
                  {formErrors.zone && <p className="text-red-500 text-xs mt-1">{formErrors.zone}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    State Code (Optional)
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    placeholder="e.g. TN"
                    value={form.statecode}
                    onChange={(e) => setForm({ ...form, statecode: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Status</label>
                  <select
                    value={form.status ? 'true' : 'false'}
                    onChange={(e) => setForm({ ...form, status: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">Enabled (Active)</option>
                    <option value="false">Disabled (Inactive)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow transition flex items-center gap-2"
                >
                  {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <span>{editingState ? 'Update State' : 'Create State'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: View Details */}
      {viewState && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold">{viewState.statename}</h3>
              <button onClick={() => setViewState(null)} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Zone</span>
                  <strong className="text-gray-800 text-base">{viewState.zone || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">State Code</span>
                  <strong className="font-mono text-blue-600 text-base">{viewState.statecode || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Status</span>
                  <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold ${viewState.status !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {viewState.status !== false ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setViewState(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewState;
                  setViewState(null);
                  handleOpenModal(target);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateMaster;